import { useState } from 'react';
import { EmailDomainGetStatusResult } from '@mews-commander/data-access';
import { WithEnterpriseId } from '@mews-commander/types';
import { lt, showSuccessAlert } from '@mews/framework';
import { OverlayedLoader } from '@mews-commander/ui';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { AddEmailSenderPage } from './AddEmailSenderPage';
import { deleteDnsSettings, setEmail, useEmailDomainStatus, verifyDnsSettings } from './apiHooks';
import { EmailSenderFormValues } from './EmailSenderForm';
import { EmailSenderStatusPage } from './EmailSenderStatusPage';

export const EmailSender = ({ enterpriseId }: WithEnterpriseId) => {
    const { data: initialStatusData, fetchData: reloadStatus } = useEmailDomainStatus({ enterpriseId });
    const [updatedData, setUpdatedData] = useState<Partial<EmailDomainGetStatusResult>>({});
    const dispatch = useCommanderThunkDispatch();
    const [verifyLoaded, setVerifyLoaded] = useState(false);
    const reload = async () => {
        setUpdatedData({});
        setVerifyLoaded(false);
        await reloadStatus();
    };

    const updateData = (data: Partial<EmailDomainGetStatusResult>) =>
        setUpdatedData((currentData) => ({ ...currentData, ...data }));

    if (initialStatusData == null) {
        return <OverlayedLoader show />;
    }

    const statusData = { ...initialStatusData, ...updatedData };
    const { email, dnsRecords, verified, sendGridDomainId } = statusData;

    const handleAddForm = async (values: EmailSenderFormValues) => {
        const { value } = await dispatch(setEmail({ email: values.email, enterpriseId }));
        updateData({ ...value, email: values.email, verified: false });
    };

    if (email == null) {
        return <AddEmailSenderPage onSubmit={handleAddForm} />;
    }

    const handleVerify = async () => {
        const { value } = await dispatch(verifyDnsSettings({ sendGridDomainId: sendGridDomainId ?? 0, enterpriseId }));
        updateData(value);
        setVerifyLoaded(true);

        if (value.verified) {
            showSuccessAlert({
                description: lt('DnsSettingsVerified', { email }),
            });
        }
    };

    const handleDelete = async () => {
        await dispatch(deleteDnsSettings({ sendGridDomainId: sendGridDomainId ?? 0, enterpriseId }));
        await reload();
    };

    return (
        <EmailSenderStatusPage
            {...{ email, dnsRecords, verified, verifyLoaded }}
            onVerify={handleVerify}
            onDelete={handleDelete}
        />
    );
};
